import { useCallback, useEffect, useState } from 'react';

import { monitorApi } from '../api/monitor';
import type {
  Account,
  AccountChanges,
  AccountStatusFilter,
  NewAccount,
  OperatorOption,
} from '../api/monitor';

export interface AccountsController {
  accounts: Account[];
  operators: OperatorOption[];
  /** Aba aberta; `null` enquanto `/operators` não respondeu. */
  operator: OperatorOption | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  /** Situação filtrada; a tela abre nas ativas. */
  status: AccountStatusFilter;
  selectOperator: (code: string) => void;
  setStatus: (status: AccountStatusFilter) => void;
  create: (payload: NewAccount) => Promise<Account | null>;
  /** Edita a conta e recarrega a lista — inativar pode tirá-la do filtro. */
  update: (id: number, changes: AccountChanges) => Promise<Account | null>;
  reload: () => void;
}

export function useAccounts(): AccountsController {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [operators, setOperators] = useState<OperatorOption[]>([]);
  const [operatorCode, setOperatorCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  // Ativas por padrão: conta de teste ou de senha vencida, depois de
  // inativada, sai da frente sem sumir de vez.
  const [status, setStatus] = useState<AccountStatusFilter>('active');

  // As abas são as operadoras cadastradas: a primeira delas abre a tela.
  useEffect(() => {
    monitorApi
      .operators()
      .then((response) => {
        setOperators(response.operators);
        setOperatorCode((current) => current ?? response.operators[0]?.code ?? null);
      })
      .catch((cause: Error) => {
        setError(cause.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!operatorCode) return;

    let current = true;
    setLoading(true);

    monitorApi
      .accounts(operatorCode, status)
      .then((response) => {
        if (!current) return;
        setAccounts(response.accounts);
        setError(null);
      })
      .catch((cause: Error) => {
        if (!current) return;
        setError(cause.message);
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [operatorCode, status, reloadToken]);

  const create = useCallback(async (payload: NewAccount) => {
    setSaving(true);
    try {
      const response = await monitorApi.createAccount(payload);
      // O servidor devolve a lista da operadora cadastrada, já com a conta nova.
      setAccounts(response.accounts);
      setOperatorCode(payload.operatorCode);
      setError(null);

      return response.created;
    } finally {
      setSaving(false);
    }
  }, []);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const update = useCallback(async (id: number, changes: AccountChanges) => {
    setSaving(true);
    try {
      const response = await monitorApi.updateAccount(id, changes);
      setReloadToken((token) => token + 1);

      return response.account;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    accounts,
    operators,
    operator: operators.find((item) => item.code === operatorCode) ?? null,
    loading,
    error,
    saving,
    status,
    selectOperator: setOperatorCode,
    setStatus,
    create,
    update,
    reload,
  };
}
